// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, react-hooks/exhaustive-deps, react-hooks/immutability */

import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import * as farmApi from "../../../../apis/farmApi";
import { FarmData } from "../../../../apis/types/farmTypes";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth.js";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";
import ResultsTable from "../../../Lumber/Search/ResultsTable";
import SearchPanel from "../../../Lumber/Search/SearchPanel";
import { AnyFilter, FilterOption } from "../../../Lumber/Search/types";
import { generateStringFilter } from "../../../../utilities/search";

const FarmSearch = () => {
	const DATE_FORMAT = "MM/DD/YYYY h:mma";
	const moduleNavigation = useModuleNavigation();
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();

	const tableHeaders = {
		farmName: "Farm",
		code: "Code",
		cooperatorName: "Cooperator",
		location: "Location",
		cropNames: "Crops",
		lastFlightDate: "Last Flight Date",
		dateRange: "Date Range",
		activeFields: "Active Fields",
		activeTrials: "Active Trials",
	};

	const getFarms = async (clientId: string) => {
		const accessToken = await getTokenSilently();
		const json = farmApi.getFarmsAsync(accessToken, clientId);
		return json;
	};

	const { data: searchedData, isFetching } = useQuery({
		queryKey: ["farms search", userAuth.currentClientId],
		queryFn: () => getFarms(userAuth.currentClientId!),
		select: (data) => data.data,
	});
	const [data, setData] = useState<FarmData[]>([]);
	const [filters, setFilters] = useState<{ [key in keyof FarmData]?: AnyFilter }>({});
	const [dropdownOpts, setDropdownOpts] = useState<{ [key in keyof FarmData]?: FilterOption[] }>({});
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		if (searchedData) {
			//-- Setting agnostic error flag
			_.map(searchedData, (sd) => {
				sd.error = !sd.parcelsExist;
			});

			setData(
				_.sortBy(
					_.map(searchedData, (sd) => ({
						...sd,
						location: {
							city: sd.city,
							region: sd.region,
							country: sd.country,
							countryCode: sd.countryCode,
						},
					})),
					"code",
				),
			);
		}
	}, [searchedData]);

	useEffect(() => {
		setDropdownOpts({
			cropNames: getCropOptions(data),
			cooperatorName: getCooperatorOptions(data),
			country: getCountryOptions(data),
			region: getRegionOptions(data),
			city: getCityOptions(data),
		});
	}, [data]);

	const getCropOptions = useCallback((farmData: FarmData[]) => {
		// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
		const cropSet = farmData.reduce((acc, red) => {
			const union = _.union(
				acc,
				red.cropNames.filter((c) => c !== null),
			);
			return union;
		}, []);
		// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
		cropSet.sort();

		// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
		const cropOpts: FilterOption[] = cropSet.map((c, i) => ({
			text: c,
			key: "crops-" + i,
			value: c,
		}));

		cropOpts.push({
			text: "No Crops",
			key: "crops-null",
			value: null,
		});

		return cropOpts;
	}, []);

	const getCooperatorOptions = useCallback((farmData: FarmData[]) => {
		const cooperatorSet = _.sortBy(farmData, (f) => f.cooperatorName).reduce((acc, red) => {
			acc.add(red.cooperatorName);
			return acc;
		}, new Set<string>());

		const coopOps: FilterOption[] = Array.from(cooperatorSet).map((coop, i) => ({
			text: coop,
			key: "coops-" + i,
			value: coop,
		}));

		return coopOps;
	}, []);

	const getCountryOptions = useCallback((farmData: FarmData[]) => {
		const countrySet = _.sortBy(farmData, (f) => f.country).reduce((acc, red) => {
			acc.add(red.country);
			return acc;
		}, new Set<string>());

		const countryOps: FilterOption[] = Array.from(countrySet).map((coop, i) => ({
			text: coop,
			key: "country-" + i,
			value: coop,
		}));

		return countryOps;
	}, []);

	const getRegionOptions = useCallback((farmData: FarmData[]) => {
		const regionSet = _.sortBy(farmData, (f) => f.region).reduce((acc, red) => {
			acc.add(red.region);
			return acc;
		}, new Set<string>());

		const regionOps: FilterOption[] = Array.from(regionSet).map((coop, i) => ({
			text: coop,
			key: "region-" + i,
			value: coop,
		}));

		return regionOps;
	}, []);

	const getCityOptions = useCallback((farmData: FarmData[]) => {
		const citySet = _.sortBy(farmData, (f) => f.city).reduce((acc, red) => {
			acc.add(red.city);
			return acc;
		}, new Set<string>());

		const cityOps: FilterOption[] = Array.from(citySet).map((coop, i) => ({
			text: coop,
			key: "cities-" + i,
			value: coop,
		}));

		return cityOps;
	}, []);

	const handleFilters = {
		cropNames: generateStringFilter("cropNames", setFilters),
		cooperatorName: generateStringFilter("cooperatorName", setFilters),
		country: generateStringFilter("country", setFilters),
		region: generateStringFilter("region", setFilters),
		city: generateStringFilter("city", setFilters),
	};

	const onResetFilters = () => {
		setDropdownOpts({
			cropNames: getCropOptions(data),
			cooperatorName: getCooperatorOptions(data),
			country: getCountryOptions(data),
			region: getRegionOptions(data),
			city: getCityOptions(data),
		});
		setSearchTerm("");
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<SearchPanel onReset={onResetFilters}>
				<SearchPanel.Bar
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
					placeholder="Search Farms"
					disabled={data.length === 0}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Cooperators"
					options={dropdownOpts.cooperatorName ?? []}
					// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
					setFilters={handleFilters.cooperatorName}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Crops"
					size="small"
					options={dropdownOpts.cropNames ?? []}
					// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
					setFilters={handleFilters.cropNames}
				/>
				<SearchPanel.MultiDropdown
					placeholder="City"
					size="small"
					options={dropdownOpts.city ?? []}
					// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
					setFilters={handleFilters.city}
				/>
				<SearchPanel.MultiDropdown
					placeholder="State / Region"
					size="small"
					options={dropdownOpts.region ?? []}
					// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
					setFilters={handleFilters.region}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Country"
					size="small"
					options={dropdownOpts.country ?? []}
					// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
					setFilters={handleFilters.country}
				/>
			</SearchPanel>

			<ResultsTable<FarmData>
				level="farm"
				loading={isFetching}
				data={data}
				// setData={setData}
				headers={tableHeaders}
				onRenderDataColumn={{
					cropNames: (cn) => (cn[0] !== null ? cn : undefined),
					lastFlightDate: (lfd) => (lfd ? moment(lfd).format(DATE_FORMAT) : undefined),
					dateRange: (dr) => (dr !== "" ? dr : undefined),
					location: ({ city, region, countryCode }) => `${city}, ${region}, ${countryCode}`,
					activeFields: (af) => af.length.toString(),
					activeTrials: (at) => at.length.toString(),
				}}
				onSortColumn={{
					farmName: true,
					code: true,
					cooperatorName: true,
					city: true,
					region: true,
					location: (a, b) =>
						(a.country ?? "").localeCompare(b.country ?? "") ||
						(a.region ?? "").localeCompare(b.region ?? "") ||
						(a.city ?? "").localeCompare(b.city ?? ""),
					lastFlightDate: (a, b) => moment(b).diff(moment(a)),
				}}
				columnFilters={filters}
				actions={[
					{
						hasArea: () => true,
						title: "Viewer",
						icon: "binoculars",
						to: (f) => moduleNavigation.createFarmLink(false, f.farmId, "viewer"),
					},
					{
						hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.FarmEdit, row.farmClientId),
						title: "Edit",
						icon: "pencil",
						to: (f) => moduleNavigation.createFarmLink(false, f.farmId, "edit"),
					},
					// {
					// 	icon: "sun",
					// 	subIcon: "cloud",
					// 	action: (f) => moduleNavigation.createFarmLink(false, f.farmId, "weather")
					// }
				]}
				links={{
					farmName: (f) => moduleNavigation.createFarmLink(false, f.farmId, "viewer"),
				}}
				searchTerm={{ term: searchTerm, cols: ["farmName"] }}
			/>
		</div>
	);
};

export default FarmSearch;
